
import { ReactNode, useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../lib/auth.context'
import { useToast } from '../../hooks/use-toast'

interface ProtectedRouteProps {
  children: ReactNode
  requiresAuth?: boolean
  requiresRecoverySession?: boolean
  requiresInviteSession?: boolean
}

export function ProtectedRoute({ 
  children, 
  requiresAuth = true,
  requiresRecoverySession = false,
  requiresInviteSession = false
}: ProtectedRouteProps) {
  const { isAuthenticated, session, refreshSession, isInitializing, isRecoverySession, isInviteSession, setIsInviteSession } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { toast } = useToast()

  // Check for invite link on component mount
  useEffect(() => {
    // Debug logging to see what's in the URL
    console.log('[ProtectedRoute] URL Debug - pathname:', location.pathname, 'search:', location.search, 'hash:', location.hash);
    
    // Check URL parameters for invite-related parameters
    const params = new URLSearchParams(location.search);
    
    // Handle double hash format: #/auth/complete-invite#access_token=...&type=invite
    let hashParams = new URLSearchParams();
    if (location.hash) {
      const hash = location.hash;
      const secondHashIndex = hash.indexOf('#', 1); // Find second # after the first one
      if (secondHashIndex !== -1) {
        // Double hash - extract parameters from the second part
        const paramsPart = hash.substring(secondHashIndex + 1);
        hashParams = new URLSearchParams(paramsPart);
      } else {
        // Single hash - try to parse as parameters
        hashParams = new URLSearchParams(hash.replace('#', ''));
      }
    }
    
    // Check for invite-specific parameters that indicate a real invite link
    const hasInviteType = params.get('type') === 'invite' || hashParams.get('type') === 'invite';
    const hasCode = params.has('code'); // PKCE code
    const hasAccessToken = hashParams.has('access_token'); // Hash-based tokens
    
    console.log('[ProtectedRoute] Invite Detection - hasInviteType:', hasInviteType, 'hasCode:', hasCode, 'hasAccessToken:', hasAccessToken);
    
    // Check if we are on the complete-invite page
    const isCompleteInvitePage = location.pathname === '/auth/complete-invite' ||
                                  location.pathname.includes('/auth/complete-invite');
    
    // Only set invite session if we have invite type AND some form of tokens
    // OR if we're directly on the complete-invite page (for manual navigation)
    if (hasInviteType && (hasCode || hasAccessToken) && !isInviteSession) {
      console.log('[ProtectedRoute] Detected invite link with tokens, setting invite session');
      setIsInviteSession(true);
    } else if (isCompleteInvitePage && !isInviteSession) {
      // Allow direct navigation to complete-invite page for testing/manual access
      console.log('[ProtectedRoute] User on complete-invite page, allowing access for validation');
      setIsInviteSession(true);
    } else if (hasInviteType && !hasCode && !hasAccessToken) {
      // User has invite type but no tokens - this is likely an expired/invalid link
      console.log('[ProtectedRoute] Detected invite type without tokens - likely expired link');
      toast({
        title: 'Expired Invite Link',
        description: 'Your invitation link has expired. Please contact an administrator for a new invitation.',
        variant: 'destructive',
      });
      navigate('/login', { replace: true });
    }
  }, [location, isInviteSession, setIsInviteSession, navigate, toast]);


  useEffect(() => {
    const checkAuthAndSession = async () => {
      try {
        // Wait for token initialization before checking auth state
        if (isInitializing) {
          console.log('[ProtectedRoute] Waiting for initialization...')
          return
        }

        console.log('[ProtectedRoute] Checking auth state:', {
          isAuthenticated,
          isInitializing,
          isRecoverySession,
          isInviteSession,
          currentPath: location.pathname
        })

        console.log('[ProtectedRoute] Access control - requiresAuth:', requiresAuth, 
                    'requiresRecoverySession:', requiresRecoverySession,
                    'requiresInviteSession:', requiresInviteSession,
                    'isAuthenticated:', isAuthenticated, 
                    'isRecoverySession:', isRecoverySession,
                    'isInviteSession:', isInviteSession);
        
        const isResetPasswordPage = location.pathname === '/auth/reset-password' || 
                                   location.pathname === '/reset-password' ||
                                   location.pathname.includes('/auth/reset-password');
        
        const isCompleteInvitePage = location.pathname === '/auth/complete-invite' ||
                                     location.pathname.includes('/auth/complete-invite');
        
        // Special case: Page specifically requires a recovery session
        if (requiresRecoverySession) {
          console.log('[ProtectedRoute] Page requires recovery session');
          
          if (!isRecoverySession) {
            console.log('[ProtectedRoute] User not in recovery session but page requires it, redirecting to login');
            toast({
              title: 'Access Denied',
              description: 'This page is only accessible when resetting your password.',
              variant: 'destructive'
            });
            navigate('/login', { replace: true });
            return;
          }
          
          // User is in recovery session and page requires it - allow access
          console.log('[ProtectedRoute] User in recovery session and page requires it - allowing access');
        }
        // Special case: Page specifically requires an invite session
        else if (requiresInviteSession) {
          console.log('[ProtectedRoute] Page requires invite session');
          
          if (!isInviteSession) {
            console.log('[ProtectedRoute] User not in invite session but page requires it, redirecting to login');
            toast({
              title: 'Access Denied',
              description: 'This page is only accessible with a valid invitation link.',
              variant: 'destructive'
            });
            navigate('/login', { replace: true });
            return;
          }
          
          // User is in invite session and page requires it - allow access
          console.log('[ProtectedRoute] User in invite session and page requires it - allowing access');
        }
        // If user is in recovery session but page doesn't specifically require it
        else if (isAuthenticated && isRecoverySession) {
          // Only allow access to reset password pages during recovery session
          if (!isResetPasswordPage) {
            console.log('[ProtectedRoute] User in recovery session, redirecting to reset password page from:', location.pathname)
            toast({
              title: 'Password Reset Required',
              description: 'Please complete your password reset before accessing the application.',
            });
            navigate('/auth/reset-password', { replace: true });
            return;
          }
        }
        // If the page is not the complete-invite page but an invite session is detected
        else if (isInviteSession && !isCompleteInvitePage && !requiresInviteSession) {
          // Extract only the auth parameters from the hash, not the route part
          const hashToPreserve = location.hash;
          let authParams = '';
          
          if (hashToPreserve) {
            const secondHashIndex = hashToPreserve.indexOf('#', 1);
            if (secondHashIndex !== -1) {
              // Double hash - extract only the auth parameters
              authParams = '#' + hashToPreserve.substring(secondHashIndex + 1);
            } else {
              // Single hash - preserve as is
              authParams = hashToPreserve;
            }
          }
          
          console.log('[ProtectedRoute] User in invite session, redirecting to complete-invite page from:', location.pathname, 'with auth params:', authParams);
          toast({
            title: 'Complete Registration',
            description: 'Please complete your registration before accessing the application.',
          });
          
          // Use React Router navigate for proper SPA routing
          navigate(`/auth/complete-invite${authParams}`, { replace: true });
          return;
        }
        // Normal authentication check
        else if (requiresAuth && !isAuthenticated && !isInviteSession) {
          console.log('[ProtectedRoute] Not authenticated, redirecting to login');
          
          // Store current path for redirect after login
          const currentPath = location.pathname + location.search;
          localStorage.setItem('redirectUrl', currentPath);
          
          // Show toast notification
          toast({
            title: 'Authentication required',
            description: 'Please log in to access this page.',
          });
          
          navigate('/login');
          return;
        }

        // Check session validity and refresh if needed
        if (session?.expires_at) {
          const expiresInSeconds = session.expires_at - Math.floor(Date.now() / 1000)
          const shouldRefresh = expiresInSeconds < 300 // 5 minutes threshold

          if (shouldRefresh) {
            try {
              await refreshSession()
              console.log('[ProtectedRoute] Session refreshed successfully')
            } catch (error) {
              console.error('[ProtectedRoute] Failed to refresh session:', error)
              localStorage.setItem('redirectUrl', location.pathname + location.search)
              
              toast({
                title: 'Session expired',
                description: 'Your session has expired. Please log in again.',
                variant: 'destructive',
              })
              
              navigate('/login')
            }
          }
        }
      } catch (error) {
        console.error('[ProtectedRoute] Error checking auth state:', error)
        navigate('/login')
      }
    }

    checkAuthAndSession()
    
    // Set up periodic session checks
    const intervalId = setInterval(checkAuthAndSession, 60000) // Check every minute
    return () => clearInterval(intervalId)
  }, [isAuthenticated, navigate, location, session, refreshSession, isInitializing, isRecoverySession, isInviteSession, requiresAuth, requiresRecoverySession, requiresInviteSession, toast])

  // Don't render anything while initializing
  if (isInitializing) {
    return null
  }

  // Determine if access should be granted based on requirements
  const isAuthSatisfied = !requiresAuth || isAuthenticated;
  const isRecoverySatisfied = !requiresRecoverySession || isRecoverySession;
  const isInviteSatisfied = !requiresInviteSession || isInviteSession;
  
  console.log('[ProtectedRoute] Render decision - authSatisfied:', isAuthSatisfied, 
              'recoverySatisfied:', isRecoverySatisfied,
              'inviteSatisfied:', isInviteSatisfied);
  
  // Only render children if all access requirements are met
  return (isAuthSatisfied && isRecoverySatisfied && isInviteSatisfied) ? <>{children}</> : null
}
